// ============================================================
// Command Center route contract — THE single source of truth for
// the canonical Square Code Command Center URL space.
//
// Owner decision (2026-07-19): the approved Final Hub Center is
// the ONLY Square Code Command Center. Its canonical visible root
// is /square/command-center. No component may hard-code either the
// canonical root or the retired aliases — import from here.
//
// UI routes ≠ API routes: /api/square/command-center/* backend
// namespaces are canonical engines and are NOT governed by this
// file.
// ============================================================

export const COMMAND_CENTER_ROOT = '/square/command-center'

/** Build a canonical command-center path. commandCenterPath('projects') → /square/command-center/projects */
export function commandCenterPath(sub = ''): string {
  if (!sub) return COMMAND_CENTER_ROOT
  return `${COMMAND_CENTER_ROOT}${sub.startsWith('/') ? sub : `/${sub}`}`
}

/**
 * Retired roots that must 301-style redirect (replace navigation,
 * child path + query + hash preserved) into the canonical root.
 * These must never render a second command-center tree.
 */
export const COMMAND_CENTER_COMPATIBILITY_ALIASES = [
  '/square-code-v2-command-center',
  '/square/command-center-v2',
  '/command-center',
  '/dashboard',
] as const

/**
 * Legacy (retired MainframeShell command-center) bookmarks → the
 * closest canonical area. A redirect preserves access to the
 * business capability; it does NOT claim the destination has a
 * finished replacement feature — destinations use honest
 * capability states. Keys are subpaths under COMMAND_CENTER_ROOT.
 *
 * Paths the hub now owns outright (notifications, agents,
 * calendar, projects, operations/client-intake …) need no entry —
 * the canonical route simply renders there.
 */
export const LEGACY_COMMAND_CENTER_REDIRECTS: Record<string, string> = {
  // Packet-mapped
  overview: '',
  requests: 'operations',
  status: 'operations',
  workflows: 'operations',
  'client-intake': 'operations/client-intake',
  'meeting-mode': 'communications/conference-calls',
  'agent-workspace': 'agents',
  activation: 'agents',
  'project-board': 'projects',
  files: 'workspaces/vault',
  'document-builder': 'workspaces/creative',
  'dual-pane-editor': 'workspaces/creative',
  'inline-edit': 'workspaces/creative',
  'word-doc-analysis': 'workspaces/vault',
  'spreadsheet-builder': 'workspaces/finance',
  'formula-library': 'workspaces/finance',
  'integrations/excel': 'workspaces/finance',
  'pr-factory': 'workspaces/engineering',
  providers: 'operations',
  'usage-meter': 'operations',
  // Remaining retired module routes (same philosophy: nearest area)
  legacy: '',
  strategy: 'workspaces/creative',
  'creative-studio': 'workspaces/creative',
  'post-jobs': 'operations',
  'engine-v4': 'agents',
  'quantum-calendar': 'calendar',
  'calendar-workspace': 'calendar',
  graphify: 'operations',
  damages: 'workspaces/vault',
  vault: 'workspaces/vault',
  documents: 'workspaces/vault',
  tasks: 'team',
  alerts: 'notifications',
  // Retired special-case Arbuckle fork → the live portfolio
  'projects/arbuckle': 'projects',
}
